import { EppTableComponent } from '../../epp/epp-table.component';

import { EppService } from '../../epp/epp.service';

import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';

import { EppData } from '../models/epp-data.interface';
import { EppDataForecast } from '../models/epp-data-forecast.type';
import { EppDataRegistry } from '../models/epp-data-registry.interface';
import moment from 'moment';
import { Moment } from 'moment';
import { isNil } from 'src/utils/utils';

export const MODULES = [CommonModule];
export const COMPONENTS = [EppTableComponent];

@Component({
  selector: 'app-pigeons-epp',
  templateUrl: './pigeons-epp.component.html',
  styleUrls: ['./pigeons-epp.component.scss'],
  imports: [...MODULES, ...COMPONENTS],
})
export class PigeonsEppComponent implements OnInit {
  private injector = inject(Injector);
  private eppService = inject(EppService);

  parameterset = input<string>();
  userParameters = input<any>();
  parameterDefinitions = input<any>();
  stationId = input<number>();
  day = input<Moment>();
  hour = input<number>();
  forecastDtg = output<Moment>();

  eppFullData = [];
  eppDataOfPast: EppDataForecast[] = [];
  eppData = signal<EppDataForecast[]>(undefined);

  eppDataRegistry: EppDataRegistry = {};

  isLoading = true;
  lastIssueTime = '';
  showPast = false;

  constructor() {
    this.listenToStationIdChanges();
    this.listenToDayChanges();
  }

  ngOnInit() {
    this.initComponent();
  }

  async initComponent() {
    await this.eppService.getColorMappingNames();
    await this.eppService.getColorMappings();
    this.getEppData();
    this.getEppDataOfPast();
  }

  onRefreshEppData() {
    const parameterset = this.parameterset();
    const stationId = this.stationId();
    this.removeCurrentEppDataRegistry(parameterset, stationId, 5, 1);
    this.removeCurrentEppDataRegistry(parameterset, stationId, -1, 1);
    this.eppData.set(undefined);
    this.eppFullData = [];
    this.eppDataOfPast = [];
    this.lastIssueTime = '';
    this.getEppData();
    this.getEppDataOfPast();
  }

  getEppData(): void {
    const parameterset = this.parameterset();
    const stationId = this.stationId();
    this.ensureEppDataRegistryInit(parameterset, stationId, 5);
    const cachedEppData = this.eppDataRegistry[parameterset][stationId][5][1];

    const setEppData = (eppData: EppData) => {
      this.lastIssueTime = eppData.last_issue_time;
      this.eppFullData = eppData.forecast;
      if (!this.showPast) {
        this.eppData.set(undefined);
        this.eppData.set([...this.cleanUpEppData()]);
      }
      this.forecastDtg.emit(moment(eppData.last_issue_time));
    };

    if (!isNil(cachedEppData)) {
      setEppData(cachedEppData);
      return;
    }

    this.eppService
      .getEppData(parameterset, stationId, 5, 1)
      .then((eppResponse: EppData) => {
        this.eppDataRegistry[parameterset][stationId][5][1] = eppResponse;

        const lastIssueTime = eppResponse.last_issue_time;
        if (lastIssueTime !== this.lastIssueTime) {
          this.isLoading = true;
          setEppData(eppResponse);
          this.isLoading = false;
        }
      });
  }

  getEppDataOfPast(): void {
    const parameterset = this.parameterset();
    const stationId = this.stationId();
    this.ensureEppDataRegistryInit(parameterset, stationId, -1);
    const cachedEppData = this.eppDataRegistry[parameterset][stationId][-1][1];

    const setEppData = (eppData: EppData) => {
      this.eppDataOfPast = eppData.forecast;
      if (this.showPast) {
        this.eppData.set(undefined);
        this.eppData.set([...this.cleanUpEppData()]);
      }
    };
    if (!isNil(cachedEppData)) {
      setEppData(cachedEppData);
      return;
    }

    this.eppService
      .getEppData(parameterset, stationId, -1, 1)
      .then((eppResponse: EppData) => {
        this.eppDataRegistry[parameterset][stationId][-1][1] = eppResponse;

        this.isLoading = true;
        setEppData(eppResponse);
        this.isLoading = false;
      });
  }

  cleanUpEppData(): Array<any> {
    const cleanedData = [];
    const day = this.day();
    const startDtg = this.showPast
      ? moment().subtract(2, 'hours').set({ minute: 0, second: 0 })
      : moment(day).set({ hour: 0 });
    const endDtg = this.showPast
      ? moment().set({ minute: 0, second: 0 })
      : moment(day).set({ hour: 23 });

    const data = this.showPast ? this.eppDataOfPast : this.eppFullData;
    for (const dataset of data) {
      const dtg = moment(dataset['valid_at']).utc();
      const dtgLocale = moment(dataset['valid_at']);
      if (
        dtgLocale.isSameOrAfter(startDtg, 'hours') &&
        dtgLocale.isSameOrBefore(endDtg, 'hours')
      ) {
        cleanedData.push(dataset);
      }
    }
    return cleanedData;
  }

  private ensureEppDataRegistryInit(
    parameterSet: string,
    stationId: number,
    days: number
  ) {
    if (!this.eppDataRegistry[parameterSet]) {
      this.eppDataRegistry[parameterSet] = {};
    }
    if (!this.eppDataRegistry[parameterSet][stationId]) {
      this.eppDataRegistry[parameterSet][stationId] = {};
    }
    if (!this.eppDataRegistry[parameterSet][stationId][days]) {
      this.eppDataRegistry[parameterSet][stationId][days] = {};
    }
  }

  private removeCurrentEppDataRegistry(
    parameterSet: string,
    stationId: number,
    days: number,
    resolution: number
  ) {
    if (
      !isNil(
        this.eppDataRegistry?.[parameterSet]?.[stationId]?.[days]?.[resolution]
      )
    ) {
      this.eppDataRegistry[parameterSet][stationId][days][resolution] = null;
    }
  }

  private listenToStationIdChanges() {
    effect(
      () => {
        this.stationId();

        untracked(() => {
          this.eppData.set(undefined);
          this.eppFullData = [];
          this.eppDataOfPast = [];
          this.lastIssueTime = '';
          this.getEppData();
          this.getEppDataOfPast();
        });
      },
      { injector: this.injector }
    );
  }

  private listenToDayChanges() {
    effect(
      () => {
        const day = this.day();

        untracked(() => {
          this.showPast = day === null;
          if (this.eppFullData.length > 0 && this.eppDataOfPast.length > 0) {
            this.eppData.set(undefined);
            this.eppData.set([...this.cleanUpEppData()]);
          }
        });
      },
      { injector: this.injector }
    );
  }
}
